import { ProgressKind, RequestKind, boolean, string } from '@sb/library/base';
import {
    BatchResultItem,
    BatchSocialMediaLinksUpdateItems,
    BetaSettingsDisplayItem,
    BetaSettingsInputItem,
    InvoiceLogoDisplayItem,
    SchoolAccountingSettingsUpdateItem,
    SchoolDisplayItem,
    SchoolEmailSettingsUpdateItem,
    SchoolEventPolicySettingsUpdateItem,
    SchoolInputItem,
    SchoolInvoiceSettingsUpdateItem,
    SchoolPaymentSettingsUpdateItem,
    SchoolSMSProviderUpdateItem,
    SchoolStudentPortalSettingsUpdateItem,
    SchoolUpdateItem,
    SocialMediaLinkDisplayItem,
    SocialMediaLinkInputItem
} from '@sb/library/data-models';
import { ApiRoute } from '../api-route';

export namespace SchoolsRoutes {
    export const Add = ApiRoute.Define({
        URL: 'schools',
        Kind: RequestKind.Post,
        BodyType: SchoolInputItem,
        ResultType: string
    });

    export const Get = ApiRoute.Define({
        URL: 'school',
        Kind: RequestKind.Get,
        ResultType: SchoolDisplayItem,
        RequestedFieldsType: SchoolDisplayItem,
        Cancellable: false
    });

    export const GetPaymentMethods = ApiRoute.Define({
        URL: 'school/paymentmethods',
        Kind: RequestKind.Get,
        ResultType: Array<string>
    });

    export const GetDefaultBalanceDate = ApiRoute.Define({
        URL: 'school/balancedate',
        Kind: RequestKind.Get,
        ResultType: Date
    });

    export const Update = ApiRoute.Define({
        URL: 'school',
        Kind: RequestKind.Patch,
        BodyType: SchoolUpdateItem,
        ResultType: boolean
    });

    export namespace Settings {
        export const UpdateAccounting = ApiRoute.Define({
            URL: 'school/accountingsettings',
            Kind: RequestKind.Patch,
            BodyType: SchoolAccountingSettingsUpdateItem,
            ResultType: boolean,
            Cancellable: false
        });

        export const UpdateEmail = ApiRoute.Define({
            URL: 'school/emailsettings',
            Kind: RequestKind.Patch,
            BodyType: SchoolEmailSettingsUpdateItem,
            ResultType: boolean
        });

        export const UpdateEventPolicy = ApiRoute.Define({
            URL: 'school/eventpolicysettings',
            Kind: RequestKind.Patch,
            BodyType: SchoolEventPolicySettingsUpdateItem,
            ResultType: boolean,
            Cancellable: false
        });

        export const UpdateInvoice = ApiRoute.Define({
            URL: 'school/invoicesettings',
            Kind: RequestKind.Patch,
            BodyType: SchoolInvoiceSettingsUpdateItem,
            ResultType: boolean
        });

        export const UpdatePayment = ApiRoute.Define({
            URL: 'school/paymentsettings',
            Kind: RequestKind.Patch,
            BodyType: SchoolPaymentSettingsUpdateItem,
            ResultType: boolean
        });

        export const UpdateStudentPortal = ApiRoute.Define({
            URL: 'school/studentportalsettings',
            Kind: RequestKind.Patch,
            BodyType: SchoolStudentPortalSettingsUpdateItem,
            ResultType: boolean,
            Cancellable: false
        });

        export const UpdateSMSProvider = ApiRoute.Define({
            URL: 'school/smsprovidersettings',
            Kind: RequestKind.Patch,
            BodyType: SchoolSMSProviderUpdateItem,
            ResultType: boolean
        });

        export const UpdateInvoiceSettings = ApiRoute.Define({
            URL: 'school/invoicesettings',
            BodyType: SchoolInvoiceSettingsUpdateItem,
            Kind: RequestKind.Patch,
            ResultType: boolean,
            Cancellable: false
        });

        export const UpdateProfilePicture = ApiRoute.Define({
            URL: `school/invoicelogo`,
            Kind: RequestKind.Patch,
            BodyType: FormData,
            ResultType: string
        });

        export namespace Beta {
            export const Get = ApiRoute.Define({
                URL: 'school/beta',
                Kind: RequestKind.Get,
                ResultType: BetaSettingsDisplayItem
            });

            export const Update = ApiRoute.Define({
                URL: 'school/beta',
                Kind: RequestKind.Patch,
                BodyType: BetaSettingsInputItem,
                ResultType: boolean
            });
        }
    }

    export const Delete = ApiRoute.Define({
        URL: 'school',
        Kind: RequestKind.Delete,
        ResultType: boolean
    });

    export namespace InvoiceLogo {
        export const Delete = ApiRoute.Define({
            URL: 'school/invoicelogo',
            Kind: RequestKind.Delete,
            ResultType: boolean
        });

        export const Get = ApiRoute.Define({
            URL: 'school/invoicelogo',
            Kind: RequestKind.Get,
            ResultType: InvoiceLogoDisplayItem
        });

        export const Update = ApiRoute.Define({
            URL: 'school/invoicelogo',
            Kind: RequestKind.Patch,
            BodyType: FormData,
            ResultType: string
        });
    }

    export const CanAddTeacher = ApiRoute.Define({
        URL: 'school/canaddteacher',
        Kind: RequestKind.Get,
        ResultType: boolean
    });

    export const GetReferralUrl = ApiRoute.Define({
        URL: 'school/referralurl',
        Kind: RequestKind.Get,
        ResultType: string
    });

    export const GetSchoolName = ApiRoute.Define({
        URL: 'names/school',
        Kind: RequestKind.Get,
        ResultType: string
    });

    export namespace SocialLinks {
        export const GetAll = ApiRoute.Define({
            URL: 'school/sociallinks',
            Kind: RequestKind.Get,
            ResultType: Array<SocialMediaLinkDisplayItem>
        });

        export const Delete = ApiRoute.Define({
            URL: (socialLinkID: any) => `school/sociallinks/${socialLinkID}`,
            Kind: RequestKind.Delete,
            ResultType: boolean
        });

        export const Add = ApiRoute.Define({
            URL: 'school/sociallinks',
            Kind: RequestKind.Post,
            BodyType: SocialMediaLinkInputItem,
            ResultType: string
        });

        export const BatchUpdate = ApiRoute.Define({
            URL: 'school/sociallinks',
            Kind: RequestKind.Patch,
            BodyType: BatchSocialMediaLinksUpdateItems,
            ResultType: Array<BatchResultItem<boolean, never>>,
            ShowProgress: ProgressKind.Computation
        });
    }
}
